import { useState } from "react";
import { useOcrExtractions, OcrReviewStatus, OCR_STATUS_LABEL, OCR_STATUS_BADGE_CLASS } from "@/api/ocr";
import OcrExtractionModal from "@/components/ocr/OcrExtractionModal";

const STATUS_FILTERS: Array<OcrReviewStatus | ""> = ["", "PENDING", "FAILED", "EDITED", "APPROVED", "REJECTED"];

export default function OcrReviewPage() {
  const [statusFilter, setStatusFilter] = useState<OcrReviewStatus | "">("PENDING");
  const { data, isLoading } = useOcrExtractions({ status: statusFilter || undefined, pageSize: 50 });
  const [openExtractionId, setOpenExtractionId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Revisión OCR</h1>
        <p className="mt-1 text-sm text-gray-500">
          Datos extraídos automáticamente de documentos de imagen (RUT, cámara de comercio, facturas). Ningún dato se
          usa en el sistema hasta que alguien lo revisa aquí.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((status) => (
          <button
            key={status || "ALL"}
            type="button"
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              statusFilter === status ? "bg-brand-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setStatusFilter(status)}
          >
            {status ? OCR_STATUS_LABEL[status] : "Todas"}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Documento</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Categoría</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Confianza</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Recibido</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && (data?.data ?? []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  No hay extracciones OCR en este filtro.
                </td>
              </tr>
            )}
            {(data?.data ?? []).map((extraction) => (
              <tr key={extraction.id}>
                <td className="px-4 py-3 font-medium text-gray-900">{extraction.document?.originalName ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{extraction.document?.category ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{extraction.confidence ? `${Number(extraction.confidence).toFixed(0)}%` : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${OCR_STATUS_BADGE_CLASS[extraction.status]}`}>
                    {OCR_STATUS_LABEL[extraction.status]}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{new Date(extraction.createdAt).toLocaleString("es-CO")}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    className="text-brand-600 hover:text-brand-800 text-xs font-medium"
                    onClick={() => setOpenExtractionId(extraction.id)}
                  >
                    Ver / revisar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openExtractionId && <OcrExtractionModal extractionId={openExtractionId} onClose={() => setOpenExtractionId(null)} />}
    </div>
  );
}
